<script setup lang="ts">
import { UI_ZONES, zoneNumber, type UiZone } from "./uiChangesZones";

/**
 * Schéma de l'écran de l'outil dessin (pas une capture : il reste juste quand
 * un détail change, suit le thème et fonctionne hors ligne). Chaque zone se
 * touche ; la zone choisie ressort, les autres s'estompent.
 */
defineProps<{ selected: UiZone }>();
const emit = defineEmits<{ select: [zone: UiZone] }>();

/** Pastille numérotée de chaque zone, là où le schéma est vide. */
const BADGES: Record<UiZone, { x: number; y: number }> = {
  header: { x: 250, y: 12 },
  notes: { x: 250, y: 32 },
  photo: { x: 196, y: 56 },
  toolbar: { x: 28, y: 120 },
  tabs: { x: 286, y: 58 },
  selection: { x: 234, y: 199 },
};

const NOTE_COLORS = ["#f97316", "#22c55e", "#eab308", "#a855f7", "#ef4444"];
</script>

<template>
  <svg
    class="screen-map"
    viewBox="0 0 320 210"
    role="group"
    :aria-label="$t('canvas:uiChanges.screen.map')"
  >
    <rect class="screen" x="0.5" y="0.5" width="319" height="209" rx="10" />
    <!-- Le plan, en fond -->
    <g class="plan">
      <polyline points="60,160 60,90 150,90 150,130 230,130 230,170" />
      <polyline points="90,175 260,175" />
      <rect x="170" y="100" width="40" height="22" />
      <circle cx="105" cy="130" r="14" />
    </g>

    <g
      v-for="zone in UI_ZONES"
      :key="zone"
      class="zone"
      :class="{ selected: selected === zone, dimmed: selected !== zone }"
      role="button"
      tabindex="0"
      :aria-label="$t(`canvas:uiChanges.screen.zones.${zone}.title`)"
      :aria-pressed="selected === zone"
      @click="emit('select', zone)"
      @keydown.enter.prevent="emit('select', zone)"
      @keydown.space.prevent="emit('select', zone)"
    >
      <template v-if="zone === 'header'">
        <path
          class="bar"
          d="M10.5 0.5 H309.5 A10 10 0 0 1 319.5 10.5 V24 H0.5 V10.5 A10 10 0 0 1 10.5 0.5 Z"
        />
        <rect class="ink" x="8" y="7" width="10" height="10" rx="2" />
        <rect class="ink" x="22" y="10" width="38" height="4" rx="2" />
        <rect class="chip" x="68" y="7" width="30" height="10" rx="5" />
        <rect class="chip" x="102" y="7" width="24" height="10" rx="5" />
        <rect
          v-for="i in 3"
          :key="i"
          class="chip"
          :x="134 + (i - 1) * 30"
          y="8"
          width="26"
          height="8"
          rx="4"
        />
        <circle class="ink" cx="276" cy="12" r="3" />
        <circle class="ink" cx="287" cy="12" r="3" />
        <rect class="chip" x="296" y="8" width="16" height="8" rx="4" />
      </template>
      <template v-else-if="zone === 'notes'">
        <rect class="bar" x="0.5" y="24" width="319" height="16" />
        <rect
          v-for="i in 10"
          :key="i"
          :x="8 + (i - 1) * 22"
          y="28"
          width="16"
          height="8"
          rx="2"
          :fill="NOTE_COLORS[i % NOTE_COLORS.length]"
          class="note"
        />
      </template>
      <template v-else-if="zone === 'photo'">
        <rect
          class="bar floating"
          x="136"
          y="48"
          width="48"
          height="16"
          rx="5"
        />
        <rect class="accent" x="140" y="51" width="14" height="10" rx="3" />
        <rect class="ink" x="158" y="53" width="8" height="6" rx="1" />
        <rect class="ink" x="170" y="53" width="10" height="6" rx="1" />
      </template>
      <template v-else-if="zone === 'toolbar'">
        <path
          class="bar floating"
          d="M0.5 66 H12 A5 5 0 0 1 17 71 V169 A5 5 0 0 1 12 174 H0.5 Z"
        />
        <rect
          v-for="i in 8"
          :key="i"
          :class="i === 2 ? 'accent' : 'ink'"
          x="4"
          :y="70 + (i - 1) * 13"
          width="9"
          height="9"
          rx="2"
        />
      </template>
      <template v-else-if="zone === 'tabs'">
        <rect
          v-for="i in 5"
          :key="i"
          class="bar floating"
          x="300"
          :y="48 + (i - 1) * 18"
          width="15"
          height="15"
          rx="4"
        />
      </template>
      <template v-else>
        <path
          class="bar floating selection-bar"
          d="M100 209.5 V195 A5 5 0 0 1 105 190 H217 A5 5 0 0 1 222 195 V209.5 Z"
        />
        <rect
          v-for="i in 5"
          :key="i"
          class="ink"
          :x="106 + (i - 1) * 15"
          y="195"
          width="10"
          height="10"
          rx="2"
        />
        <rect class="accent" x="188" y="195" width="28" height="10" rx="3" />
      </template>
      <circle class="badge" :cx="BADGES[zone].x" :cy="BADGES[zone].y" r="7" />
      <text
        class="badge-text"
        :x="BADGES[zone].x"
        :y="BADGES[zone].y + 3.2"
        text-anchor="middle"
      >
        {{ zoneNumber(zone) }}
      </text>
    </g>
  </svg>
</template>

<style lang="scss" scoped>
.screen-map {
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
}

.screen {
  fill: rgba(255, 255, 255, 0.04);
  stroke: var(--canvas-border);
}

.plan {
  fill: none;
  stroke: rgba(255, 255, 255, 0.14);
  stroke-width: 1.2;
}

.zone {
  cursor: pointer;
  outline: none;
  transition: opacity 0.15s ease;

  &.dimmed {
    opacity: 0.45;
  }

  &:hover,
  &:focus-visible {
    opacity: 1;
  }
}

.bar {
  fill: #2b2f36;
  stroke: var(--canvas-border);

  .zone.selected & {
    fill: var(--canvas-btn-mode-bg);
    stroke: var(--canvas-btn-active-bg);
    stroke-width: 1.5;
  }
}

.selection-bar {
  stroke: var(--canvas-btn-mode-border);
}

.ink {
  fill: rgba(255, 255, 255, 0.55);
}

.chip {
  fill: rgba(255, 255, 255, 0.18);
}

.accent {
  fill: var(--canvas-btn-active-bg);
}

.badge {
  fill: rgba(255, 255, 255, 0.85);

  .zone.selected & {
    fill: var(--canvas-btn-active-bg);
  }
}

.badge-text {
  fill: #1a1a1a;
  font-size: 9px;
  font-weight: 700;
}
</style>
